// Three modals used by the V2d tracking screen. Grouped in one file because
// they all share the same react-bootstrap Modal wrapper and presentation
// conventions. Kept at feature granularity — we don't need three files for
// three similar modals.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import Select from "react-select";
import styled from "styled-components";
import moment from "moment-timezone";
import "moment/locale/fr";
import { tokens } from "./tokens";
import { _defaultBooking } from "../../../../utils/booking";

// ────────────────────────────────────────────────────────────────
// CommentSheet — driver comment at end of ride
// ────────────────────────────────────────────────────────────────

const CommentTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  font-size: 14px;
  border: 1px solid ${tokens.colors.border};
  border-radius: ${tokens.radii.md}px;
  font-family: inherit;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: ${tokens.colors.violet};
  }
`;

export function CommentSheet({ show, onHide, value, onChange, saved, onSubmit }) {
  const dirty = (saved || "") !== (value || "");
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Commentaire fin de trajet</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CommentTextarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          placeholder="Ex. client à l'heure, trajet sans incident…"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSubmit();
            onHide();
          }}
          disabled={!dirty}
        >
          Envoyer
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────
// UpdateDriverSheet — reassign this booking to another driver.
// Ported from the former UpdateDriverModal in trackContent.js.
// ────────────────────────────────────────────────────────────────

export function UpdateDriverSheet({
  show,
  onHide,
  driverOptions,
  booking,
  submit,
  fetching,
}) {
  const selectRef = useRef(null);
  const [driver, setDriver] = useState(booking?.driver);

  useEffect(() => {
    setDriver(booking?.driver);
  }, [booking?.driver]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Modifier le chauffeur</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="driver">
            <Form.Label>Chauffeur</Form.Label>
            <Select
              ref={selectRef}
              options={driverOptions}
              value={
                driver?.id
                  ? { value: driver.id, label: driver.name, data: driver }
                  : null
              }
              onChange={(obj) => {
                setDriver(obj?.data || _defaultBooking.driver);
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={fetching}>
          Fermer
        </Button>
        <Button
          variant="primary"
          onClick={(e) => {
            e.preventDefault();
            submit(driver);
          }}
          disabled={fetching}
        >
          {fetching ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────
// BonDeMissionSheet — read-only modal showing the VTC mission order
// text in the same format as the WhatsApp notification. Rendered
// client-side from the booking data available via /track/:uid/:token.
// SIREN/REVTC/company address are NOT in that response today — when we
// need full parity with WhatsApp those fields must be added server-side.
// ────────────────────────────────────────────────────────────────

const BonPreWrap = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.5;
  background: ${tokens.colors.surfaceAlt};
  color: ${tokens.colors.text};
  padding: 14px 16px;
  border-radius: ${tokens.radii.md}px;
  border: 1px solid ${tokens.colors.border};
  max-height: 60vh;
  overflow-y: auto;
  margin: 0;
`;

function formatBonDeMission(bookingRaw, booking) {
  if (!bookingRaw) return "";
  const fr = (m) => m?.locale("fr");

  const pickupMoment = fr(booking?.pickup_date?.dateMoment);
  const pickupTime = pickupMoment ? pickupMoment.format("DD/MM/YYYY [à] HH:mm") : "";
  const createdAt = bookingRaw.created_at
    ? moment(bookingRaw.created_at * 1000)
        .tz("Europe/Paris")
        .locale("fr")
        .format("DD/MM/YYYY [à] HH:mm")
    : "";

  const driver = booking?.driver || "";
  const driverPhone = booking?.driver_phone || "";
  const chauffeurLine = (driver || driverPhone)
    ? `Chauffeur: ${[driver, driverPhone].filter(Boolean).join(" ")}`
    : "";

  const companyName = bookingRaw.driver?.company?.name || "";
  const companyAdresse = bookingRaw.driver?.company?.adresse || "";
  const companyRevtc = bookingRaw.driver?.company?.revtc || "";
  const companySiren = bookingRaw.driver?.company?.siren || "";
  const regTail = [
    companyRevtc ? `REVTC: ${companyRevtc}` : "",
    companySiren ? `SIREN: ${companySiren}` : "",
  ]
    .filter(Boolean)
    .join(" — ");
  const exploitantLines = [
    "*Exploitant*",
    companyName,
    companyAdresse,
    chauffeurLine,
    regTail,
  ].filter(Boolean);

  const passengerName = `${booking?.firstname || ""} ${booking?.lastname || ""}`.trim();
  const passengerPhone = booking?.phone || "";
  const clientLine =
    passengerName +
    (!bookingRaw.hide_phone && passengerPhone ? ` — ${passengerPhone}` : "");

  const pickupAddr = booking?.pickup_address || "";
  const dropoffAddr = booking?.dropoff_address || "";
  const pickupFull = bookingRaw.pickup?.full_address;
  const pickupFullLine =
    pickupFull && pickupFull !== pickupAddr ? `\nAdresse complète: ${pickupFull}` : "";
  const dropoffFull = bookingRaw.dropoff?.full_address;
  const dropoffFullLine =
    dropoffFull && dropoffFull !== dropoffAddr
      ? `\nAdresse complète: ${dropoffFull}`
      : "";
  const stopLine = bookingRaw.step_address ? `\nArrêt: ${bookingRaw.step_address}` : "";

  const extras = [
    bookingRaw.flight_number ? `Vol: ${bookingRaw.flight_number}` : "",
    bookingRaw.vehicle ? `Véhicule: ${bookingRaw.vehicle}` : "",
    (bookingRaw.passengers || bookingRaw.bags)
      ? `${bookingRaw.passengers || 0} passagers — ${bookingRaw.bags || 0} bagages cabine`
      : "",
    bookingRaw.additional_info ? `*Informations:* ${bookingRaw.additional_info}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    `*Bon de mission VTC* — Réf. ${bookingRaw.id || ""}\n` +
    `_Justificatif de réservation préalable (Arrêté 6 août 2025)_\n\n` +
    (exploitantLines.length ? `${exploitantLines.join("\n")}\n\n` : "") +
    `*Client*\n${clientLine}\n\n` +
    `*Réservation*\n` +
    (createdAt ? `Commandée le: ${createdAt}\n` : "") +
    `Prise en charge: *${pickupTime}*\n` +
    `Lieu: *${pickupAddr}*${pickupFullLine}${stopLine}\n\n` +
    `━━━━━━━━━━━━\n` +
    `Destination: ${dropoffAddr}${dropoffFullLine}\n` +
    extras
  );
}

export function BonDeMissionSheet({ show, onHide, bookingRaw, booking }) {
  const text = useMemo(
    () => formatBonDeMission(bookingRaw, booking),
    [bookingRaw, booking]
  );

  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* no clipboard access — ignore silently */
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Bon de mission VTC</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <BonPreWrap>{text}</BonPreWrap>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCopy}>
          {copied ? "Copié ✓" : "Copier le texte"}
        </Button>
        <Button variant="primary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────
// ConfirmSheet — reusable confirmation modal for destructive or
// state-change actions. Replaces the raw window.confirm prompts.
// ────────────────────────────────────────────────────────────────

export function ConfirmSheet({ pending, onResolve }) {
  if (!pending) return null;
  const danger = pending.tone === "danger";
  return (
    <Modal show onHide={() => onResolve(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>{pending.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{pending.body}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onResolve(false)}>
          Annuler
        </Button>
        <Button
          variant={danger ? "danger" : "primary"}
          onClick={() => onResolve(true)}
        >
          {pending.confirmLabel || "Confirmer"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
